import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import type { DownloadOfferBlock } from "@containers/Hermes/types";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import { countCache } from "@containers/Hermes/hooks/useOfferCount";
import type { CountedOffer } from "@containers/Hermes/hooks/useOfferCount";
import ActionCardShell, { SelectedCompaniesChip } from "./ActionCardShell";
import HermesCheckbox from "./HermesCheckbox";

/**
 * The download card, opened from an action bar's "Download". ChatShell renders
 * it in place of the composer while it is open.
 *
 *   open ──▶ count known? ──no──▶ /downloads/count ──▶ range + cost ──▶ /downloads/request
 *
 * A question that named its own size ("top 50 exporters") arrives with the
 * count filled in and `exact_size` set: there is no bigger set to choose from,
 * so the card shows no range — just the count and the cost.
 *
 * The cost is always on screen when the user commits. `spec_token` is a
 * signed, short-lived grant from the backend fixing the query, dataset and
 * columns; the range can narrow within it but never widen it, so the charge is
 * bounded by what was previewed and entitlement-checked.
 */

// At or above this many records the file is emailed rather than returned
// inline. Matches checkForEmailOrDownload() in tabHeader.js.
const EMAIL_THRESHOLD = 500;

// Kept for the session so reopening a card does not forget which ranges were already downloaded
// (collecting those again is free — the backend serves the stored file).
const settledRanges = new Map<string, Set<string>>();

type DownloadCardProps = {
  block: DownloadOfferBlock;
  onClose: () => void;
  modal?: boolean;
  // Rows the answer's own table shows, and how to browse all of the records instead.
  previewCount?: number;
  onCustomise?: () => void;
  // A download of just the rows the user ticked: records from..to. The range is fixed — to change it, discard and tick again.
  initialRange?: { from: number; to: number };
  lockRange?: boolean;
  // Download exactly these companies (master ids) instead of a range.
  companyIds?: string[];
  // Download exactly these rows (bucket keys) instead of a range, for a table that is not about companies.
  rowKeys?: string[];
  // The companies the answer's table shows, for the pill on a card opened from the bar beneath it.
  shownCompanies?: { key: string; name: string }[];
};

export default function DownloadCard({ block, onClose, modal, previewCount: tablePreviewCount = 0, onCustomise, initialRange, lockRange = false, companyIds, rowKeys, shownCompanies = [] }: DownloadCardProps) {
  const analytics = useHermesAnalytics();
  // The rows the answer's table shows: from the bar beneath it, or carried by an offer shown again for it.
  const previewCount = block.preview_rows || tablePreviewCount;
  const unitNoun = block.unit_noun || "records";
  // A range chosen from ticked rows is honoured even for an exact-size answer.
  const exactSize = Boolean(block.exact_size) && !block.count_pending && !initialRange && !companyIds && !rowKeys;

  const [counted, setCounted] = useState<CountedOffer | null>(() => countCache.get(block.spec_token) ?? (block.count_pending ? null : { available: block.record_count ?? 0, maxRows: block.max_rows ?? 0, creditsPerRow: block.credits_per_row ?? 1 }));
  const [countError, setCountError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // The count is not resolved when the answer renders — counting is the only
  // part of the offer that queries Elasticsearch, so only an opened card pays
  // for it.
  useEffect(() => {
    if (counted) return;
    let cancelled = false;
    axios
      .post(getApiUrl("downloads/count"), { spec_token: block.spec_token })
      .then(({ data }) => {
        const payload = data?.data ?? {};
        const totals = { available: payload.deliverable_records ?? 0, maxRows: payload.max_rows ?? block.max_rows ?? 0, creditsPerRow: payload.credits_per_row ?? 1 };
        countCache.set(block.spec_token, totals);
        if (!cancelled) setCounted(totals);
      })
      .catch((err) => {
        // The backend returns a user-facing sentence for every expected refusal
        // (no matching records, expired offer, entitlement).
        const detail = err?.response?.data?.message;
        if (!detail) Sentry.captureException(err);
        if (!cancelled) setCountError(detail || "The record count could not be read. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [counted, block.spec_token, block.max_rows]);

  const available = counted?.available ?? 0;
  const maxRows = counted?.maxRows ?? block.max_rows ?? 0;
  const creditsPerRow = counted?.creditsPerRow ?? block.credits_per_row ?? 1;
  // What "all" can actually mean, once the per-request cap is applied.
  const selectableMax = maxRows > 0 ? Math.min(available, maxRows) : available;

  const [selectAll, setSelectAll] = useState(!initialRange);
  const [fromValue, setFromValue] = useState(initialRange ? String(initialRange.from) : "1");
  const [toValue, setToValue] = useState(initialRange ? String(initialRange.to) : "");
  useEffect(() => {
    if (!initialRange) setToValue(String(selectableMax));
  }, [selectableMax, initialRange]);

  const parsed = useMemo(() => {
    const from = parseInt(fromValue, 10);
    const to = parseInt(toValue, 10);
    return { from: Number.isNaN(from) ? null : from, to: Number.isNaN(to) ? null : to };
  }, [fromValue, toValue]);

  // Opened from the bar beneath a table: it downloads the rows that table shows, and says so with a pill.
  // The range / "all" choice belongs to the full table's Download (and to a download with no table behind it).
  const fromPreview = !modal && !companyIds && !rowKeys && !initialRange && previewCount > 0;
  // Rows of a table that is not about companies are downloaded by their bucket keys, the ones ticked or, from the
  // bar beneath the table, every row it shows that has one.
  const shownKeys = useMemo(() => (block.row_keys ?? []).filter((key): key is string => Boolean(key)), [block.row_keys]);
  const keys = rowKeys ?? (fromPreview && shownKeys.length > 0 ? shownKeys : undefined);

  const range = keys ? { fromRecord: 1, size: keys.length } : fromPreview ? { fromRecord: 1, size: selectableMax > 0 ? Math.min(previewCount, selectableMax) : previewCount } : companyIds ? { fromRecord: 1, size: companyIds.length } : exactSize || selectAll ? { fromRecord: 1, size: selectableMax } : { fromRecord: parsed.from ?? 1, size: parsed.from !== null && parsed.to !== null && parsed.to >= parsed.from ? parsed.to - parsed.from + 1 : 0 };

  const rangeError = useMemo(() => {
    if (!counted || exactSize || selectAll || companyIds || keys || fromPreview) return "";
    if (parsed.from === null || parsed.to === null) return `Enter both a start and an end ${singular(unitNoun)} number.`;
    if (parsed.from < 1) return "The starting number must be 1 or greater.";
    if (parsed.to < parsed.from) return "The end number must not be before the start number.";
    if (parsed.from > available) return `This result has ${format(available)} ${unitNoun}, so ${format(parsed.from)} is beyond the end of it.`;
    if (maxRows > 0 && range.size > maxRows) return `You can download a maximum of ${format(maxRows)} ${unitNoun} at a time.`;
    return "";
  }, [counted, exactSize, selectAll, companyIds, keys, fromPreview, parsed, available, maxRows, range.size, unitNoun]);

  const rangeKey = keys ? `keys:${[...keys].sort().join("|")}` : companyIds ? `ids:${[...companyIds].sort().join(",")}` : `${range.fromRecord}:${range.size}`;
  const alreadyDownloaded = settledRanges.get(block.spec_token)?.has(rangeKey) ?? false;
  const credits = Math.round(range.size * creditsPerRow);
  // A repeat is returned as a link from storage, so the email notice does not apply to it.
  const willEmail = range.size >= EMAIL_THRESHOLD && !alreadyDownloaded;
  const canDownload = Boolean(counted) && range.size > 0 && rangeError === "";

  // The total only matters when there is more than the answer's table shows; when the
  // preview already holds every record there is nothing to point out.
  const showsTotal = Boolean(counted) && !exactSize && !companyIds && !rowKeys && available > 0 && (previewCount === 0 || available > previewCount);

  const handleDiscard = () => {
    if (busy) return;
    onClose();
  };

  const handleDownload = async () => {
    if (busy || !canDownload) return;
    setBusy(true);
    setError("");
    analytics.downloadRequested({ downloadType: block.download_type, recordCount: range.size, credits, delivery: willEmail ? "email" : "link" });

    try {
      const { data } = await axios.post(getApiUrl("downloads/request"), {
        spec_token: block.spec_token,
        ...(companyIds ? { company_ids: companyIds } : keys ? { row_keys: keys } : { from_record: range.fromRecord, size: range.size }),
      });
      const payload = data?.data ?? {};
      // Opened rather than navigated to, so the conversation stays put.
      if (payload.download_url) window.open(payload.download_url, "_blank", "noopener,noreferrer");
      const settled = settledRanges.get(block.spec_token) ?? new Set<string>();
      settled.add(rangeKey);
      settledRanges.set(block.spec_token, settled);

      const outcome = payload.message || (payload.download_url ? "Your file has been downloaded." : "We'll email your file shortly.");
      if (payload.download_url && !payload.replayed) toast.success(outcome);
      else toast.info(outcome);
      onClose();
    } catch (err: any) {
      // The backend returns a user-facing sentence for every expected refusal
      // (insufficient credits, size limit, daily allowance, expired offer, no
      // matching records), so prefer its wording over anything invented here.
      const detail = err?.response?.data?.message;
      if (!detail) Sentry.captureException(err);
      const failure = detail || "The download could not be prepared. Please try again.";
      setError(failure);
      toast.error(failure);
      setBusy(false);
    }
  };

  // The cost sits in the footer, opposite the Download button.
  const costNote =
    counted && range.size > 0 && !rangeError ? (
      alreadyDownloaded ? (
        <>
          Already downloaded — <span className="tw-font-semibold">free</span> to get again
        </>
      ) : (
        <>
          <span className="tw-font-semibold">{format(credits)}</span> download credits will be consumed
        </>
      )
    ) : null;

  return (
    <ActionCardShell modal={modal} footerNote={costNote} title={showsTotal ? `The results contains total of ${format(available)} ${unitNoun}` : modal ? "Download" : `Download ${unitNoun}`} primaryLabel={alreadyDownloaded ? "Download again" : "Download"} busyLabel="Preparing…" busy={busy} primaryDisabled={!canDownload} onPrimary={handleDownload} onDiscard={handleDiscard}>
      <div className="tw-mt-3 tw-text-sm tw-text-gray-700">
        {!counted ? (
          countError ? (
            <p role="alert" className="tw-m-0 tw-text-red-600">
              {countError}
            </p>
          ) : (
            <p role="status" className="tw-m-0 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-25 tw-px-3 tw-py-2 tw-text-gray-500">
              <span aria-hidden="true" className="tw-w-3.5 tw-h-3.5 tw-shrink-0 tw-rounded-full tw-border-2 tw-border-solid tw-border-gray-200 tw-border-t-primary-purple-500 tw-animate-spin" />
              Checking how many {unitNoun} this result has…
            </p>
          )
        ) : fromPreview ? (
          <SelectedCompaniesChip companies={shownCompanies} selectedKeys={new Set(shownCompanies.map((company) => company.key))} count={range.size} noun={unitNoun} onCustomise={onCustomise} />
        ) : exactSize || lockRange || companyIds || rowKeys ? (
          // Nothing to choose: the answer asked for exactly this many, or the user already
          // ticked the rows — so no "all / range" controls, just what will be downloaded.
          <p className="tw-m-0">
            You are downloading <span className="tw-font-semibold">{format(lockRange || companyIds || rowKeys ? range.size : selectableMax)}</span> {(lockRange || companyIds || rowKeys ? range.size : selectableMax) === 1 ? singular(unitNoun) : unitNoun}.
          </p>
        ) : (
          <>
            <p className="tw-m-0">
              This result has <span className="tw-font-semibold">{format(available)}</span> {unitNoun}
              {maxRows > 0 && available > maxRows ? (
                <>
                  {" "}
                  — you can download up to <span className="tw-font-semibold">{format(maxRows)}</span> at a time
                </>
              ) : null}
              .
            </p>

            {/*   ☑ All N companies
                  ──────── or ────────
                  Range  [from] – [to]
                Typing a range unticks "All"; ticking "All" again ignores it. */}
            <div className="tw-mt-3 tw-flex tw-flex-col tw-items-start tw-max-w-[360px]">
              <HermesCheckbox
                id={`hermes-download-all-${block.spec_token.slice(-8)}`}
                checked={selectAll}
                disabled={lockRange}
                onChange={(e) => setSelectAll(e.target.checked)}
                label={
                  <span className="tw-text-gray-900">
                    All <span className="tw-font-semibold">{format(selectableMax)}</span> {unitNoun}
                  </span>
                }
              />
              <div className="tw-flex tw-items-center tw-gap-3 tw-w-full tw-my-3" aria-hidden="true">
                <span className="tw-flex-1 tw-h-px tw-bg-gray-200" />
                <span className="tw-text-xs tw-text-gray-400">or</span>
                <span className="tw-flex-1 tw-h-px tw-bg-gray-200" />
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-gray-700">Range</span>
                <input
                  type="number"
                  min={1}
                  value={fromValue}
                  disabled={busy || lockRange}
                  onChange={(e) => {
                    setSelectAll(false);
                    setFromValue(e.target.value);
                  }}
                  aria-label={`From ${singular(unitNoun)} number`}
                  className={`tw-w-24 tw-h-9 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-px-3 tw-text-sm tw-text-center tw-outline-none focus:tw-border-primary-purple-500 disabled:tw-bg-gray-50 ${selectAll ? "tw-text-gray-400 tw-bg-gray-50" : "tw-text-gray-900 tw-bg-white"}`}
                />
                <span className="tw-text-gray-500">–</span>
                <input
                  type="number"
                  min={1}
                  value={toValue}
                  disabled={busy || lockRange}
                  onChange={(e) => {
                    setSelectAll(false);
                    setToValue(e.target.value);
                  }}
                  aria-label={`To ${singular(unitNoun)} number`}
                  className={`tw-w-24 tw-h-9 tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-px-3 tw-text-sm tw-text-center tw-outline-none focus:tw-border-primary-purple-500 disabled:tw-bg-gray-50 ${selectAll ? "tw-text-gray-400 tw-bg-gray-50" : "tw-text-gray-900 tw-bg-white"}`}
                />
              </div>
            </div>
            {rangeError ? (
              <p role="alert" className="tw-m-0 tw-mt-2 tw-text-xs tw-text-red-600">
                {rangeError}
              </p>
            ) : null}
          </>
        )}

        {counted && willEmail && !rangeError ? (
          <p className="tw-m-0 tw-mt-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-leading-4 tw-text-gray-600">
            <span aria-hidden="true" className="material-symbols-outlined tw-shrink-0 tw-text-[16px] tw-leading-none tw-text-warning-500 tw-select-none">
              info
            </span>
            <span>This file has {EMAIL_THRESHOLD} or more records, so it will be emailed to you.</span>
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="tw-m-0 tw-mt-2 tw-text-xs tw-text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </ActionCardShell>
  );
}

function format(value: number): string {
  return (value ?? 0).toLocaleString();
}

function singular(noun: string): string {
  return noun.endsWith("ies") ? `${noun.slice(0, -3)}y` : noun.replace(/s$/, "");
}
